import ServerDirections from '@/config/server-directions'
import { RequestService } from '@/services/request.service'
import snakecase from 'snakecase-keys'
import camelcase from 'camelcase-keys'
import moment from 'moment'
import { ActivityReservation, NominalAttendee, NumeralAttendee } from '@/entities/ActivityReservation'
import { Activity } from '@/entities/activities/Activity'
import { AttendeeType, ReservationMethod } from '@/entities/activities/ReservationMethod'
import { ActivityGroup } from '@/entities/activities/ActivityGroup'
import { Guest } from '@/entities/Guest'
import { Room } from '@/entities/Room'
import { Order } from '@/entities/Order'
import { Currency } from '@/entities/Currency'
import { ActivityReservationLog } from '@/entities/ActivityReservationLog'
import { WebappPage } from '@/entities/webapp-pages/WebappPage'
import store from '@/store'
import camelcaseKeys from 'camelcase-keys'

const requestService = new RequestService()

export class ActivityReservationsRequests {
  listReservations (filters) {
    const { limit, offset, status, orderBy, sinceDate, sinceTime, untilDate, untilTime, type } = filters

    const route = ServerDirections.ACTIVITY_RESERVATIONS
    const method = 'get'
    function map (data) {
      const reservations = data.data.map(function (item) {
        const activity = new Activity(item.activity)
        const webappPage = new WebappPage(item.webapp_page)
        return {
          webappPage,
          activity,
          reservation: new ActivityReservation(item.activity_reservation),
          reservationMethods: item.reservation_methods.map(rM => {
            return {
              activityReservationMethod: new ReservationMethod(rM.activity_reservation_method),
              attendeeTypes: rM.attendee_types.map(aT => new AttendeeType(aT)),
              activityGroup: rM.activity_group ? new ActivityGroup(rM.activity_group) : null,
            }
          }),
          nominalAttendees: item.nominal_attendees ? item.nominal_attendees.map(nA => new NominalAttendee(nA)) : [],
          numeralAttendees: item.numeral_attendees ? item.numeral_attendees.map(nA => new NumeralAttendee(nA)) : [],
          room: item.room ? new Room(item.room) : null,
          guest: item.guest ? new Guest(item.guest) : null,
          order: item.order ? new Order(item.order) : null,
          pendingOrder: item.pending_order ? new Order(item.pending_order) : null,
        }
      })
      return {
        reservations: reservations,
        hasNext: data.meta.has_next,
      }
    }
    const params = {
      limit, offset,
    }
    params.order = orderBy
    params.since_date = sinceDate
    params.since_time = sinceTime
    params.until_date = untilDate
    params.until_time = untilTime
    if (type) {
      params.type = type
    }
    if (status) {
      params.status = status.toString() || []
    }
    Object.keys(filters)
    return requestService.makePromise(route, method, { params }, map)
  }
  getReservation (id) {
    const route = ServerDirections.ACTIVITY_RESERVATIONS_ID.replace('{activityReservationId}', id)
    const method = 'get'
    function map (data) {
      const webappPage = new WebappPage(data.webapp_page)
      return {
        webappPage,
        reservation: new ActivityReservation(data.activity_reservation),
        nominalAttendees: data.nominal_attendees ? data.nominal_attendees.map(nA => new NominalAttendee(nA)) : [],
        numeralAttendees: data.numeral_attendees ? data.numeral_attendees.map(nA => new NumeralAttendee(nA)) : [],
        room: data.room ? new Room(data.room) : null,
        activity: new Activity(data.activity),
        guest: data.guest ? new Guest(data.guest) : null,
        order: data.order ? new Order(data.order) : null,
        pendingOrder: data.pending_order ? new Order(data.pending_order) : null,
        currencies: data.currencies ? data.currencies.map(c => new Currency(c)) : [],
        reservationMethods: data.reservation_methods.map(rM => {
          return {
            activityReservationMethod: new ReservationMethod(rM.activity_reservation_method),
            attendeeTypes: rM.attendee_types.map(aT => new AttendeeType(aT)),
            activityGroup: rM.activity_group ? new ActivityGroup(rM.activity_group) : null,
          }
        }),
      }
    }
    return requestService.makePromise(route, method, {}, map)
  }
  async getTicket (reservationId, ticketId) {
    const route = ServerDirections.ACTIVITY_RESERVATIONS_ID_TICKET
      .replace('{activityReservationId}', reservationId)
      .replace('{ticketId}', ticketId)
    const token = await requestService.authHeader()
    return `${ route }?x-api-key=${ token }`
  }
  cancelReservation (data) {
    const route = ServerDirections.ACTIVITY_RESERVATIONS_ID_CANCEL.replace('{id}', data.id)
    const method = 'post'
    function map (data) {
      return data
    }
    return requestService.makePromise(route, method, { body: snakecase(data), disableStatusHandling: { 409: true } }, map)
  }
  requestModification (data) {
    const route = ServerDirections.ACTIVITY_RESERVATIONS_ID_MODIFY.replace('{id}', data.id)
    const method = 'post'
    function map (data) {
      return data
    }
    return requestService.makePromise(route, method, { body: snakecase(data) }, map)
  }
  makeReservation (data, order) {
    const method = 'post'
    function map (data) {
      return camelcaseKeys(data, { deep: true })
    }
    const comments = data.comments
    const numberOfAdults = parseInt(data.adults)
    const numberOfChildren = data.children ? parseInt(data.children) : null
    const time = moment.duration(data.slot, 'HH:mm').asMilliseconds()
    const date = moment(data.date).format('YYYY-MM-DD')
    let body = {
      activityId: data.activityId,
      guestId: data.guest.guest.id,
      accountId: data.account ? data.account.id : null,
      type: localStorage.getItem('reservationMethodType'),
      requestedDate: date,
      requestedTime: time,
      roomId: data.room ? data.room.room.id : null,
      order: order ? order.serialize() : null,
      attendees: data.attendees,
    }

    let serverDirection = ServerDirections.ACTIVITY_ID_RESERVATIONS
  
    if (data && data.paymentServiceId && data.paymentServiceId !== 'roomCharge') {
      serverDirection = ServerDirections.ACTIVITIES_ID_START_PAYMENT
      body.paymentServiceId = data.paymentServiceId
    }
    if (numberOfAdults) {
      body.numberOfAdults = numberOfAdults
    }
    if (numberOfChildren) {
      body.numberOfChildren = numberOfChildren
    }
    if (comments) {
      body.comments = comments
    }

    const route = serverDirection.replace('{activityId}', data.activityId)

    store.commit('activityReservations/setAttemptedReservation', camelcase(body))
    return requestService.makePromise(route, method, { body: snakecase(body) }, map)
  }
  retryReservation (reservation) {
    let serverDirection = ServerDirections.ACTIVITY_ID_RESERVATIONS

    if (reservation && reservation.paymentServiceId && reservation.paymentServiceId !== 'roomCharge') {
      serverDirection = ServerDirections.ACTIVITIES_ID_START_PAYMENT
    }

    const route = serverDirection.replace('{activityId}', reservation.activityId)
    const method = 'post'
    function map (data) {
      return camelcaseKeys(data, { deep: true })
    }
    return requestService.makePromise(route, method, { body: snakecase(reservation) }, map)
  }

  retryPayment (activityReservationId, paymentServiceId) {
    const serverDirection = ServerDirections.ACTIVITY_RESERVATIONS_ID_RETRY_PAYMENT
    const route = serverDirection.replace('{id}', activityReservationId)
    const method = 'post'
    const body = {
      paymentServiceId,
    }
    function map (data) {
      return camelcaseKeys(data, { deep: true })
    }
    return requestService.makePromise(route, method, { body: snakecase(body) }, map)
  }

  getHistory (id) {
    const route = ServerDirections.ACTIVITY_RESERVATIONS_HISTORY.replace('{id}', id)
    const method = 'get'
    function map (data) {
      return data.map((item) => {
        return new ActivityReservationLog(item)
      })
    }
    return requestService.makePromise(route, method, {}, map)
  }
}
